import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import classNames from 'classnames';

import s from './PodPriorityCell.module.scss';

import type { FC } from 'react';
import type { IDashboardTaskRow } from 'pages/TasksDashboard/types';

type PodContext = NonNullable<IDashboardTaskRow['podContext']>;

interface IPodPriorityCellProps {
  podContext: PodContext | null;
}

const VALID_POD_PRIORITIES = ['primary', 'secondary', 'tertiary'] as const;

const PodPriorityCell: FC<IPodPriorityCellProps> = ({ podContext }) => {
  const { t } = useTranslation();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipId = useId();

  if (!podContext || podContext.podPriority === 'outside') {
    return (
      <span className={s['outside-pod']} data-cy="pod-priority-outside">
        {t('tasksDashboard.podPriority.outsidePod')}
      </span>
    );
  }

  const {
    podName,
    podPriority,
    clinicNames,
    clinicLocations,
    patientClinicName,
  } = podContext;
  const label = t(`tasksDashboard.podPriority.${podPriority}`);
  const priorityClass = VALID_POD_PRIORITIES.includes(
    podPriority as (typeof VALID_POD_PRIORITIES)[number],
  )
    ? s[podPriority]
    : undefined;

  const tooltipContent = (
    <div id={tooltipId} className={s.tooltip}>
      <div className={s['tooltip-pod-name']}>{podName}</div>
      <div className={s['tooltip-priority']}>
        {t('tasksDashboard.podPriority.priority')}:{' '}
        <span
          className={classNames(s['tooltip-priority-value'], priorityClass)}
        >
          {label}
        </span>
      </div>
      <div>
        {t('tasksDashboard.podPriority.clinics')}:
        <ul className={s['tooltip-clinics-list']}>
          {clinicNames.map((clinic, i) => {
            const state = clinicLocations?.[i]?.state;
            return (
              <li key={`${clinic}-${i}`}>
                {clinic}
                {state && (
                  <span className={s['tooltip-clinic-state']}> ({state})</span>
                )}
                {clinic === patientClinicName && (
                  <span className={s['tooltip-clinic-patient']}>
                    {' '}
                    - {t('tasksDashboard.podPriority.patientClinic')}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );

  return (
    <Tooltip
      title={tooltipContent}
      trigger={['hover', 'focus']}
      open={tooltipOpen}
      onOpenChange={setTooltipOpen}
      overlayClassName={s['tooltip-overlay']}
    >
      <span
        className={priorityClass}
        tabIndex={0}
        role="button"
        aria-describedby={tooltipOpen ? tooltipId : undefined}
        data-cy={`pod-priority-${podPriority}`}
      >
        {label}
      </span>
    </Tooltip>
  );
};

export default PodPriorityCell;
